import { useCallback, useMemo, useState } from "react"

// Power BI style cross-filtering over a list of rows.
//
// Every chart, table and dropdown is a "dimension" (department, gender, designation...). Picking
// values in one dimension filters the rows every other dimension sees. A dimension's own counts
// ignore its own selection, so a chart keeps showing all its options (the picked ones highlighted)
// and the user can switch or add values without clearing first.

export interface CrossFilterDimension<T> {
    key: string
    label: string
    // Values of the row for this dimension. More than one = the row counts under each
    // (e.g. an employee with two designations). Use NOT_SET for missing values.
    values: (row: T) => string[]
}

export interface DimensionCount {
    value: string
    count: number
}

export type CrossFilterSelections = Record<string, string[]>

const intersects = (values: string[], selected: string[]) => values.some(v => selected.includes(v))

const useCrossFilter = <T,>(rows: T[], dimensions: CrossFilterDimension<T>[]) => {
    const [selections, setSelections] = useState<CrossFilterSelections>({})

    const byKey = useMemo(() => new Map(dimensions.map(d => [d.key, d])), [dimensions])

    // Rows that pass every selection except the one for `exceptKey`
    const rowsExcept = useCallback((exceptKey?: string) => {
        const active = Object.entries(selections)
            .filter(([key, values]) => key !== exceptKey && values.length > 0 && byKey.has(key))
        if (active.length === 0) return rows
        return rows.filter(row => active.every(([key, selected]) => intersects(byKey.get(key)!.values(row), selected)))
    }, [rows, selections, byKey])

    const filtered = useMemo(() => rowsExcept(), [rowsExcept])

    // Count per value for one dimension, over the rows the other selections allow.
    // Picked values are always listed, even when nothing else matches them any more.
    const countsFor = useCallback((key: string): DimensionCount[] => {
        const dimension = byKey.get(key)
        if (!dimension) return []
        const counts = new Map<string, number>()
        rowsExcept(key).forEach(row => {
            new Set(dimension.values(row)).forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1))
        })
        ;(selections[key] ?? []).forEach(v => { if (!counts.has(v)) counts.set(v, 0) })
        return Array.from(counts, ([value, count]) => ({ value, count }))
    }, [byKey, rowsExcept, selections])

    const selectedFor = useCallback((key: string) => selections[key] ?? [], [selections])

    // Click on a chart value. Plain click: only this value (click it again to clear).
    // additive (Ctrl/Cmd + click): add or remove it from the current selection.
    const toggle = useCallback((key: string, value: string, additive = false) => {
        setSelections(prev => {
            const current = prev[key] ?? []
            let next: string[]
            if (additive) next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
            else next = current.length === 1 && current[0] === value ? [] : [value]
            return { ...prev, [key]: next }
        })
    }, [])

    // Replace the selection of one dimension (dropdown filters)
    const select = useCallback((key: string, values: string[]) => {
        setSelections(prev => ({ ...prev, [key]: values }))
    }, [])

    const clear = useCallback((key?: string) => {
        setSelections(prev => (key ? { ...prev, [key]: [] } : {}))
    }, [])

    // Every picked value, for the "active filters" chips
    const active = useMemo(() => dimensions.flatMap(d =>
        (selections[d.key] ?? []).map(value => ({ key: d.key, label: d.label, value }))
    ), [dimensions, selections])

    return { filtered, rowsExcept, countsFor, selectedFor, toggle, select, clear, active }
}

export type CrossFilter = ReturnType<typeof useCrossFilter>

export default useCrossFilter
